import { useTranslation } from "react-i18next";

import { Button } from "./button";

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  /**
   * Both forms spelled out rather than appending an "s". Every noun this
   * takes today is regular, which is exactly how a codebase ends up with
   * "1 childs" the first time one is not.
   */
  noun: { one: string; other: string };
  onChange: (page: number) => void;
}

/**
 * The footer every list screen shares.
 *
 * The count is announced politely: someone filtering with a screen reader
 * gets told the result set changed, which a table that silently redraws
 * would never tell them.
 */
export function Pagination({ page, totalPages, total, noun, onChange }: PaginationProps) {
  const { t } = useTranslation("common");
  const nounLabel = total === 1 ? noun.one : noun.other;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs text-ink-muted" aria-live="polite">
        {total === 0
          ? t("noResults")
          : t("pageOf", {
              page,
              totalPages: Math.max(totalPages, 1),
              total,
              noun: nounLabel,
            })}
      </p>

      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          {t("previous")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          {t("next")}
        </Button>
      </div>
    </div>
  );
}
